import { lazy } from "react";
import { Navigate, Outlet } from "react-router-dom";

// project imports
import Loadable from "../components/Loader/Loadable";
import FullScreenLoader from "../components/Loader/FullScreenLoader";
import AuthLayout from "../layouts/AuthLayout/AuthLayout";
import { useAppSelector } from "../redux/store";
import { useGetUserProfileQuery } from "../redux/services/modules/user";
import { useGetSpProfileQuery } from "../redux/services/modules/service-provider";

const PremiumGuard = () => {
    const auth = useAppSelector((state) => state.auth);
    const role = auth?.user?.role;
    const shouldFetch = !!auth?.isLoggedIn;

    const {
        data: userProfileData,
        isLoading: userProfileInitialLoading,
    } = useGetUserProfileQuery(undefined, {
        skip: !shouldFetch || role !== "User",
    });

    const {
        data: spProfileData,
        isLoading: spProfileInitialLoading,
    } = useGetSpProfileQuery(undefined, {
        skip: !shouldFetch || role !== "Provider",
    });

    if (role === "User" && userProfileInitialLoading)
        return <FullScreenLoader />;
    if (role === "Provider" && spProfileInitialLoading)
        return <FullScreenLoader />;
    if (role !== "User" && role !== "Provider") {
        return <Navigate to="/subscription" replace />;
    }

    const premiumFlag =
        role === "User"
            ? userProfileData?.results?.is_premium
            : spProfileData?.results?.is_premium;

    // Backend flag format: 1 => premium, 0 => not premium
    const isPremium =
        premiumFlag === true ||
        premiumFlag === 1 ||
        premiumFlag === "1";

    return isPremium ? <Outlet /> : <Navigate to="/subscription" replace />;
};

const DashboardLayout = Loadable(
    lazy(() => import("../pages/Dashboard/DashboardLayout"))
);
const DashboardAccount = Loadable(
    lazy(() => import("../pages/Dashboard/Account"))
);
const PlanAndBillings = Loadable(
    lazy(() => import("../pages/Dashboard/PlanAndBillings"))
);
const DashboardBookings = Loadable(
    lazy(() => import("../pages/Dashboard/Bookings"))
);
const DashboardFavorites = Loadable(
    lazy(() => import("../pages/Dashboard/FavoritesPage"))
);
const DashboardNotifications = Loadable(
    lazy(() => import("../pages/Dashboard/Notifications"))
);
const DashboardChatPage = Loadable(
    lazy(() => import("../pages/Dashboard/DashboardChatPage")),
);
// const DashboardAvailability = Loadable(
//     lazy(() => import("../pages/Dashboard/Availability"))
// );
const RequestDetailsPage = Loadable(
    lazy(() => import("../pages/Dashboard/bookings/RequestDetailsPage"))
);
const Page404 = Loadable(lazy(() => import("../pages/Auth/Page404")));

// ==============================|| AUTHENTICATION ROUTING ||============================== //

const PrivateRoutes = {
    path: "/dashboard",
    element: <AuthLayout />,
    children: [
        {
            element: <PremiumGuard />,
            children: [
                {
                    element: <DashboardLayout />,
                    children: [
                        {
                            index: true,
                            element: <Navigate to="bookings" replace />,
                        },
                        {
                            path: "account",
                            element: <DashboardAccount />,
                        },
                        {
                            path: "plan-and-billings",
                            element: <PlanAndBillings />,
                        },
                        {
                            path: "bookings",
                            element: <DashboardBookings />,
                        },
                        {
                            path: "favorites",
                            element: <DashboardFavorites />,
                        },
                        {
                            path: "notifications",
                            element: <DashboardNotifications />,
                        },
                        {
                            path: "chat",
                            element: <DashboardChatPage />,
                        },
                        // {
                        //     path: "availability",
                        //     element: <DashboardAvailability />,
                        // },
                        {
                            path: "bookings/request/:requestId",
                            element: <RequestDetailsPage />,
                        },
                        { path: "*", element: <Page404 to="/404" /> },
                    ],
                },
            ],
        },
    ],
};

export default PrivateRoutes;
